/**
 * Os planos e as palavras da modal de conversão.
 *
 * Fora do componente pelo mesmo motivo que plus-copy.ts está fora dos banners:
 * nenhuma palavra vive dentro de um componente.
 *
 * OS NÚMEROS VÊM DO SERVIDOR, AS PALAVRAS FICAM AQUI. lume.php emite os planos
 * activos e a campanha a decorrer (lib/plans.ts); este ficheiro guarda os nomes
 * dos separadores, os selos e a validade, que são texto de marketing e não
 * dados. A tabela FALLBACK_PRICES existe só para o caso em que o servidor não
 * mandou nada — a modal abre com preços velhos, que é melhor do que não abrir.
 */

import { paywallVariant } from "../../lib/experiment";
import { planByDuration, type LumePlan } from "../../lib/plans";

/** O ?plan= que o checkout entende — ver AutoCoupons::ALIAS_DURATIONS. */
export type PlanSlug = "monthly" | "trimestral" | "annual";

export interface ConversionPlan {
  /** O nome no separador. */
  tab: string;
  /** O nome dentro do cartão pago. */
  card: string;
  /** A linha por baixo do nome, o que este plano é. */
  seal: string;
  /** A pílula de desconto no separador. Vazia quando o plano não tem campanha. */
  tabOff: string;
  /** O selo por cima do cartão. */
  badge: string;
  price: string;
  /** O preço antes do desconto, riscado. Vazio quando não há. */
  was: string;
  value: number;
  /** Dias de acesso, para o "por dia". Derivado da duração. */
  days: number;
  validity: string;
  cta: string;
  /** O botão num telemóvel, onde fica logo a seguir ao preço. */
  ctaShort: string;
  slug: PlanSlug;
}

/** Quantos meses cada slug é — a chave que casa a cópia com o plano do servidor. */
const SLUG_DURATION: Record<PlanSlug, number> = {
  monthly: 1,
  trimestral: 3,
  annual: 12,
};

/**
 * As palavras de cada plano. O `cta` é o de base: um plano com campanha troca-o
 * por "Assinar com N% OFF", que é o número a decidir e não esta tabela.
 *
 * `campaign` substitui o selo e a validade enquanto o plano tem cupão. Só o
 * Mensal o tem: é o plano da Reta Final, e "cobre até o ENEM" só é verdade para
 * quem o compra antes de o cupão expirar.
 */
const PLAN_COPY: Record<
  PlanSlug,
  Pick<ConversionPlan, "tab" | "card" | "seal" | "badge" | "validity" | "cta"> & {
    campaign?: Pick<ConversionPlan, "seal" | "validity">;
  }
> = {
  monthly: {
    tab: "Mensal",
    card: "Plano Mensal",
    seal: "mais flexível",
    badge: "MAIS FLEXÍVEL",
    validity: "Acesso mensal. Pix Automático ou cartão.",
    cta: "Assinar TM+ Mensal",
    campaign: {
      seal: "cobre até o ENEM",
      validity:
        "1 mês de acesso. Pagamento único sem renovação automática. Pix ou cartão.",
    },
  },
  trimestral: {
    tab: "Trimestral",
    card: "Plano Trimestral",
    seal: "cobre até o ENEM",
    badge: "COBRE ATÉ O ENEM",
    validity: "Acesso de 3 meses. Pix Automático ou cartão.",
    cta: "Assinar TM+ Trimestral",
  },
  annual: {
    tab: "Anual",
    card: "Plano Anual",
    seal: "menor preço por mês",
    badge: "MENOR PREÇO POR MÊS",
    validity: "Acesso de 12 meses. Pix Automático ou cartão.",
    cta: "Assinar TM+ Anual",
  },
};

/** Só para quando window.__LUME_PLANS__ não chegou. Ver o cabeçalho. */
const FALLBACK_PRICES: Record<PlanSlug, { value: number; label: string }> = {
  monthly: { value: 59.9, label: "R$ 59,90" },
  trimestral: { value: 149.9, label: "R$ 149,90" },
  annual: { value: 349.9, label: "R$ 349,90" },
};

export const PLAN_ORDER: readonly PlanSlug[] = [
  "monthly",
  "trimestral",
  "annual",
];

/**
 * O Mensal abre escolhido, com ou sem campanha: é o plano da Reta Final e o de
 * quase todas as compras, e abrir noutro obrigava a trocar de separador para
 * ver o preço que o banner prometeu.
 */
export const DEFAULT_PLAN: PlanSlug = "monthly";

function currencyLabel(value: number): string {
  return `R$ ${value.toFixed(2).replace(".", ",")}`;
}

/**
 * O preço riscado, que são duas coisas diferentes conforme o plano.
 *
 * Com campanha é o próprio preço de tabela: o desconto é real e o riscado
 * mostra-o. Sem campanha, num plano de vários meses, é o que a mesma duração
 * custaria ao mês — a comparação que justifica o plano longo. No mensal não há
 * riscado nenhum: não há nada com que o comparar.
 */
function wasLabel(
  slug: PlanSlug,
  plan: LumePlan | null,
  monthly: LumePlan | null
): string {
  if (plan?.promoValue != null) return plan.priceLabel;

  const months = SLUG_DURATION[slug];

  if (months <= 1) return "";

  const monthlyValue = monthly?.value ?? FALLBACK_PRICES.monthly.value;

  return currencyLabel(Math.round(monthlyValue * months * 100) / 100);
}

function hasCampaign(
  plan: LumePlan | null
): plan is LumePlan & { promoValue: number; percentOff: number } {
  return plan?.promoValue != null && plan.percentOff != null;
}

/**
 * Os três planos como a modal os desenha, com os números do servidor por cima
 * da cópia. Chamado a cada abertura e não memoizado: é barato, e memoizar
 * congelaria uma campanha que acabou entre duas aberturas na mesma sessão.
 */
export function conversionPlans(): Record<PlanSlug, ConversionPlan> {
  const monthly = planByDuration(SLUG_DURATION.monthly);
  const resolved = {} as Record<PlanSlug, ConversionPlan>;

  PLAN_ORDER.forEach((slug) => {
    const months = SLUG_DURATION[slug];
    const plan = planByDuration(months);
    const fallback = FALLBACK_PRICES[slug];
    const hasPromo = hasCampaign(plan);
    const off = hasPromo ? Math.round(plan.percentOff) : 0;
    const { campaign, ...base } = PLAN_COPY[slug];
    const copy = hasPromo && campaign ? { ...base, ...campaign } : base;

    resolved[slug] = {
      ...copy,
      // A pílula do separador é SÓ para quem tem campanha a sério. O riscado do
      // anual é uma comparação com o mensal, não um desconto, e anunciá-lo na
      // barra roubaria a atenção ao único plano que está mesmo em promoção.
      tabOff: hasPromo ? `${off}% OFF` : "",
      badge: hasPromo ? `${off}% OFF · ${copy.seal.toUpperCase()}` : copy.badge,
      cta: hasPromo ? `Assinar TM+ com ${off}% OFF` : copy.cta,
      ctaShort: hasPromo ? `Assinar com ${off}% OFF` : copy.cta,
      price: hasPromo
        ? (plan?.promoLabel ?? currencyLabel(plan?.promoValue ?? fallback.value))
        : (plan?.priceLabel ?? fallback.label),
      was: wasLabel(slug, plan, monthly),
      value: hasPromo
        ? (plan?.promoValue ?? fallback.value)
        : (plan?.value ?? fallback.value),
      days: months === 12 ? 365 : months * 30,
      slug,
    };
  });

  return resolved;
}

/**
 * Os tectos do plano grátis, ditos como tectos e não como benefícios: rótulo
 * primeiro, número depois. São o espelho exacto da lista paga — mesmo eixo,
 * mesma ordem, mesmo substantivo — porque as duas colunas só se comparam de
 * relance se disserem a mesma coisa pela mesma ordem.
 */
export const FREE_LIMITS: readonly { label: string; limit: string }[] = [
  { label: "Trilha", limit: "só a 1.ª parada — 1 matéria de 14" },
  { label: "Exercícios", limit: "10 por dia" },
  { label: "Simulados", limit: "1 a cada 7 dias" },
  { label: "Correções de redação", limit: "1 a cada 30 dias, só com a C1" },
  { label: "Chat com o tutor Lume", limit: "limite diário de mensagens" },
  { label: "Navegação", limit: "com anúncios" },
];

/** Exercícios e simulados são um só — «praticar». */
export const PLUS_BENEFITS: readonly string[] = [
  "Trilha completa",
  "Exercícios e simulados sem limite",
  "Correções de redação ilimitadas",
  "Chat com o tutor Lume sem limite",
  "Estudo sem anúncios",
];

/**
 * O título da modal, que é uma das peças do lume_paywall_retafinal_v1. `word`
 * leva o gradiente da marca — ver o componente — e o braço b não tem nenhuma.
 */
export function conversionTitle(): { before: string; word: string; after: string } {
  return paywallVariant() === "b"
    ? { before: "Sua preparação para o ENEM, sem limites.", word: "", after: "" }
    : { before: "O ", word: "Lume", after: " sem limites, do primeiro tópico à prova." };
}

/** A linha por baixo do botão. */
export const CONVERSION_GUARANTEE = "Garantia de reembolso em 7 dias";

/**
 * "Menos de R$ X por dia", sempre dividido pelo período do plano.
 *
 * O arredondamento é PARA CIMA, ao menor número limpo acima do valor exacto:
 * 59,90/30 dá 1,9967, e arredondar a duas casas daria "menos de R$ 2,00" — que
 * é falso por três milésimos. Testa passos de 5, 10, 25 e 50 cêntimos e de 1
 * real e fica com o menor, que é o número mais baixo anunciável sem mentir.
 */
const STEPS = [0.05, 0.1, 0.25, 0.5, 1];

function isClean(value: number): boolean {
  return (
    Math.abs(value * 100 - Math.round(value * 100)) < 1e-6 &&
    STEPS.some((step) => Math.abs(value / step - Math.round(value / step)) < 1e-6)
  );
}

function currency(value: number): string {
  // sem cêntimos quando são zero: "R$ 2" lê-se melhor que "R$ 2,00"
  return value % 1 === 0 ? String(value) : value.toFixed(2).replace(".", ",");
}

export function perDay(plan: ConversionPlan): string {
  const exact = plan.value / plan.days;

  if (isClean(exact)) return `Equivale a R$ ${currency(exact)} por dia.`;

  const ceiling = STEPS.reduce((best, step) => {
    const candidate =
      Math.round(Math.ceil((exact - 1e-9) / step) * step * 100) / 100;

    return candidate < best ? candidate : best;
  }, Infinity);

  return `Menos de R$ ${currency(ceiling)} por dia.`;
}

/**
 * A prova social, ao fundo da modal, e a nota que a sustenta.
 *
 * A NOTA NÃO É OPCIONAL. O número é uma alegação sobre resultados de alunos, e
 * dizê-lo sem dizer de onde vem — quantos alunos, que ano, comparado com quê —
 * é o que separa uma prova de um slogan. Por isso o ⓘ está ao lado do número e
 * não numa página de termos.
 */
export const CONVERSION_PROOF = {
  before: "Assinantes do TM+ tiveram ",
  value: "+138 pontos",
  after: " acima da média nacional em 2025.",
  /** O rótulo do gatilho, para quem navega por leitor de ecrã. */
  noteLabel: "Como este número foi apurado",
  note: "Média de 107 assinantes TM+ no ENEM 2025, comparada com a média nacional do mesmo ano.",
};
